/*
 * 작성자 : 김광민
 * 날짜 : 2024.07.04
 * 기능 : 상황판에서 가로 사이즈 5 기상종합 컴포넌트
 */

import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import styled from 'styled-components';
import WeatherWideNow from './WeatherWideNow';
import WeatherWideWeek from './WeatherWideWeek';
import WeatherWideTime from './WeatherWideTime';
import useWeatherInfo from '../../hooks/useWeatherInfo';
import { IUser } from '../../atoms';
import IssueGuide from '../IssueGuide';
import { WEATHER_ERROR_MESSAGE } from '../../_constants';

const Root = styled.div`
  height: inherit;
  display: grid !important;
  grid-template-columns: repeat(3, 1fr) !important;
  gap: 1rem;
  overflow: hidden;
  background: var(--widget-bg);

  .weatherError {
    position: absolute;
    width: 100%;
    height: auto;
    > div {
      width: inherit;
      > p {
        display: flex;
        align-items: center;
        justify-content: center;
        width: inherit;
        padding: 0.25rem 0;
        color: #fff;
        background-color: rgba(0, 0, 0, 0.4);
        z-index: 100;
        border-radius: 0;
        gap: 0.25rem;

        > span.material-symbols-rounded {
          font-size: 20px;
        }
      }
    }
  }

  > div {
    display: flex;
    height: 100%;
    align-items: center;
    justify-content: center;
  }
`;

// 6일간의 날씨 데이터를 초기화
const days = Array.from({ length: 6 }).map((_, index) => {
  const targetDay = dayjs().add(index + 1, 'day'); // +1 to start from tomorrow
  return {
    order: index,
    date: targetDay.date(),
    day: targetDay.format('ddd'),
    sky: '',
  };
});

// 시간별 날씨 데이터를 초기화 현재 시간에서 8시간 뒤까지
const times = Array.from({ length: 8 }).map((_, index) => {
  const targetTime = dayjs().add(index + 1, 'hour');
  return {
    date: targetTime.format('YYYYMMDD'),
    time: `${targetTime.format('HH')}00`,
    pcp: '0',
    pop: '0',
    sky: '-',
    sno: '-',
    tmp: '0',
    wsd: '0',
  };
});

const WeatherWide = ({ userInfo, width, refetchInterval }: { userInfo: IUser; width: 'large' | 'medium'; refetchInterval: number }) => {
  const { curWeather, openThreeDaysWeather, weekWeather, tWeather, shortAdrs, isErrorCurrentWeather, isErrorTimeWeather, isErrorWeekWeather } = useWeatherInfo({
    autoRefresh: refetchInterval === undefined,
  });

  // 로컬 스토리지에서 데이터 가져오기
  const after6DaysLocalStorage = window.localStorage.getItem('after6Days');
  const currentWeatherLocalStorage = window.localStorage.getItem('currentWeatherW');
  const timeWeatherLocalStorage = window.localStorage.getItem('timeWeather');

  const localAfter6Days = after6DaysLocalStorage //
    ? JSON.parse(after6DaysLocalStorage)
    : days;

  const localCurrentWeather = currentWeatherLocalStorage //
    ? JSON.parse(currentWeatherLocalStorage)
    : { wsd: '0', tmp: '0', minTemp: '0', maxTemp: '0', rainPct: '0', sky: '-', pm10: '0', pm25: '0' };

  const localTimeWeather = timeWeatherLocalStorage //
    ? JSON.parse(timeWeatherLocalStorage)
    : times;

  const [after6Days, setAfter6Days] = useState<IDays[]>(localAfter6Days);
  const [currentWeather, setCurrentWeather] = useState(localCurrentWeather);
  const [timeWeather, setTimeWeather] = useState(localTimeWeather);

  // 사이트 정보 쿼리 결과 처리
  useEffect(() => {
    if (shortAdrs) setCurrentWeather((prev: any) => ({ ...prev, district: shortAdrs }));
  }, [shortAdrs]);

  // 현재 날씨 쿼리 결과 처리
  useEffect(() => {
    if (!isErrorCurrentWeather && curWeather) {
      const { sky, tmp, wsd } = curWeather;
      setCurrentWeather((prev: any) => ({ ...prev, sky, tmp, wsd }));
    }
  }, [isErrorCurrentWeather, curWeather]);

  // 오픈 날씨 쿼리 결과 처리
  useEffect(() => {
    if (isErrorCurrentWeather && openThreeDaysWeather) {
      setCurrentWeather((prev: any) => ({
        ...prev,
        sky: openThreeDaysWeather[0].sky,
        tmp: openThreeDaysWeather[0]?.tmp || 0,
        wsd: openThreeDaysWeather[0].wsd,
        minTemp: openThreeDaysWeather[0].minTemp,
        maxTemp: openThreeDaysWeather[0].maxTemp,
      }));
    }
  }, [isErrorCurrentWeather, openThreeDaysWeather]);

  // 주간 날씨 쿼리 결과 처리
  useEffect(() => {
    if (weekWeather) {
      setCurrentWeather((prev: any) => ({
        ...prev,
        minTemp: weekWeather.todayTmn,
        maxTemp: weekWeather.todayTmx,
        rainPct: weekWeather.rainPct,
      }));
      setAfter6Days(weekWeather.week);
      window.localStorage.setItem('after6Days', JSON.stringify(weekWeather.week));
    }
  }, [weekWeather]);

  // 시간별 날씨 쿼리 결과 처리
  useEffect(() => {
    if (tWeather) {
      const { pm10, pm25, weatherList } = tWeather;
      setCurrentWeather((prev: any) => ({ ...prev, pm10, pm25 }));
      setTimeWeather(weatherList);
      window.localStorage.setItem('timeWeather', JSON.stringify(weatherList));
    }
  }, [tWeather]);

  // 기존 날씨 시간별 API 에러시 시간 업데이트
  useEffect(() => {
    if (isErrorTimeWeather) {
      setTimeWeather(times);
      window.localStorage.setItem('timeWeather', JSON.stringify(times));
    }
  }, [isErrorTimeWeather]);

  useEffect(() => {
    if (currentWeather) {
      window.localStorage.setItem('currentWeatherW', JSON.stringify(currentWeather));
    }
  }, [currentWeather]);

  // 기존 날씨 주간 API 쿼리 에러시 시간 업데이트
  useEffect(() => {
    if (isErrorWeekWeather) {
      // 로컬 스토리지에 값이 있으면 그 값을 활용
      if (after6DaysLocalStorage) {
        const localData = JSON.parse(after6DaysLocalStorage);
        const dateSet = days.map((day) => {
          const findIdx = localData.findIndex((localDay: any) => localDay.date === day.date);
          return findIdx !== -1 ? { ...day, sky: localData[findIdx].sky, minTemp: localData[findIdx].minTemp, maxTemp: localData[findIdx].maxTemp } : day;
        });
        setAfter6Days(dateSet);
      } else {
        // 로컬 스토리지 값이 없으면 시간 업데이트
        setAfter6Days(days);
      }
    }
  }, [isErrorWeekWeather]);

  useEffect(() => {
    if (after6Days) {
      window.localStorage.setItem('after6Days', JSON.stringify(after6Days));
    }
  }, [after6Days]);

  return (
    <Root style={isErrorTimeWeather || isErrorWeekWeather || isErrorCurrentWeather ? { position: 'relative' } : { position: 'static' }}>
      {/* {isErrorTimeWeather || isErrorWeekWeather || isErrorCurrentWeather ? (
        <div className='weatherError'>
          <IssueGuide isImg={false} text={WEATHER_ERROR_MESSAGE} icon='report' />
        </div>
      ) : null} */}
      <WeatherWideNow //
        currentWeather={currentWeather}
        width={width}
      />
      <WeatherWideTime //
        timeWeather={timeWeather}
        width={width}
      />
      <WeatherWideWeek //
        after6Days={after6Days}
        width={width}
      />
    </Root>
  );
};

export default WeatherWide;
